import { Editable, Slate, withReact } from "slate-react";
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CustomText, StatusType, TaskType } from "App.types";
import { withHistory } from "slate-history";
import { createEditor, Transforms } from "slate";
import { Button, Flex } from "antd";
import API from "Api";
import { withCustomLogic } from "App.helpers";
import { CloseOutlined } from "@ant-design/icons";
import SentenceMaskedLeaf from "Components/SentenceTask/SentenceLeaf";
import styles from "Components/SentenceTask/SentenceTask.module.scss";
import cx from "classnames";
import { SentenceTaskProps } from "Components/SentenceTask/SentenceTask.type";
import { capitalize } from "lodash";
import TaskPanel from "Components/TaskPanel";
import { useQueryClient } from "@tanstack/react-query";
import { NotifyContext } from "App";

const initialValue: any[] = [{ children: [{ text: "" }] }];

const FeedbackSelectTask: FC<SentenceTaskProps> = ({
  sentence,
  sentence: { id, text, translate, alternatives, description, storyId, markers },
  lesson,
  onTaskComplete,
  onNext,
  children,
  alignCenter = false,
  noTranslate,
  noText,
  showGrammar,
}) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Editing);
  const [selectedAnswer, setSelectedAnswer] = useState<string>();
  const [activeLeaf, setActiveLeaf] = useState<number | null>(null);

  const editor = useMemo(() => withReact(withHistory(withCustomLogic(createEditor()))), []);

  const notifyApi = useContext(NotifyContext);

  const setInitialState = useCallback(() => {
    editor.children.forEach(() => {
      Transforms.delete(editor, { at: [0] });
    });

    editor.children = [];
    Transforms.insertNodes(editor, [{ children: [{ text: "", answer: text }] }]);
  }, [editor, text]);

  // initial
  useEffect(() => {
    setActiveLeaf(null);
    setStatus(StatusType.Editing);
    setInitialState();
  }, [sentence.id, editor, setInitialState]);

  const onComplete = useCallback(() => {
    setStatus(StatusType.Completed);
    onTaskComplete(selectedAnswer);
  }, [onTaskComplete, selectedAnswer]);

  const client = useQueryClient();

  const isTemplate = markers.includes("template");
  const propName = isTemplate ? markers.find((m) => m !== "template") : undefined;

  const onNextClick = useCallback(() => {
    if (!selectedAnswer && isTemplate) {
      return notifyApi.warning({ message: "Напиши что-нибудь!" });
    }

    if (propName && selectedAnswer) {
      API.user.updateProp(propName, selectedAnswer).then(() => client.invalidateQueries({ queryKey: ["props"] }));
    }

    API.feedback
      .save({
        answer: selectedAnswer ?? "",
        question: text || translate || description || "",
        lessonId: lesson.id,
        type: "taskFeedback",
        task: "select",
        sentenceId: id,
      })
      .then(onComplete)
      .then(() => onNext());
  }, [selectedAnswer, isTemplate, propName, text, translate, description, lesson.id, id, onComplete, notifyApi, client, onNext]);

  const renderLeaf = useCallback(
    (props: any) => (
      <SentenceMaskedLeaf
        showMasked
        hideMasked={status === StatusType.Completed}
        underlined={props.leaf.audioIdx === (activeLeaf ?? -1)}
        showErrors={!["editing", "completed", "loading", "isRecording", ""].includes(status)}
        {...props}
      />
    ),
    [status, activeLeaf],
  );

  const onReset = () => {
    setInitialState();
  };

  const onWordGroupClick = (t: string) => (e: any) => {
    setSelectedAnswer(t);
    setStatus(StatusType.Completed);
    const idx = editor.children[0].children.findIndex((el: CustomText) => el.isMasked);
    const word = idx === 0 && capitalize(text) === text ? capitalize(e.key || t) : e.key || t;
    Transforms.insertText(editor, word, { at: [0, idx === -1 ? 0 : idx] });
  };

  return (
    <div className={styles.sentenceTask}>
      <div className={cx(styles.content, styles.content_autoHeight)}>
        <div className={styles.children}>{children}</div>

        <div className={cx(styles.slate, { [styles.slate_alignCenter]: alignCenter })}>
          {!noText && (
            <div className={styles.slate_wrapper}>
              {initialValue && (
                <form spellCheck="false">
                  <Slate editor={editor} initialValue={initialValue}>
                    <Editable className={styles.textArea} readOnly={true} renderLeaf={renderLeaf} />
                  </Slate>
                </form>
              )}
              {status === "completed" ? (
                <div></div>
              ) : (
                <Button
                  size={"small"}
                  type={"text"}
                  className={styles.btn__clear}
                  icon={<CloseOutlined style={{ fontSize: 12 }} />}
                  onClick={onReset}
                />
              )}
            </div>
          )}
          {!noTranslate && <div className={styles.translate}>{translate}</div>}
        </div>

        <div className={styles.bottom}>
          <Flex gap={10} wrap={"wrap"} align={"center"} className={styles.select}>
            {alternatives?.map((alt) => (
              <span
                className={cx(styles.btnSelect, { [styles.btnSelect__selected]: alt.text === selectedAnswer })}
                key={alt.text}
                onClick={onWordGroupClick(alt.text)}
              >
                {alt.text}
              </span>
            ))}
          </Flex>
        </div>
      </div>

      <TaskPanel
        lessonId={lesson.id}
        task={TaskType.FeedbackSelect}
        sentId={id}
        storyId={storyId}
        showGrammar={showGrammar}
        isCompleted={!!selectedAnswer}
        onNext={onNextClick}
      ></TaskPanel>
    </div>
  );
};

export default FeedbackSelectTask;
